import { useQuery } from "@tanstack/react-query";
import { type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";
import { roundToTwoDigits } from "features/Library/Common/utils/roundToTwoDigits";
import { GenericPerformanceList } from "features/Reporting/Content/shared/GenericPerformanceList";
import { type ColumnOption } from "interfaces/columnOptions";
import { MoboLink } from "components/MoboLink/MoboLink";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import styles from "./mostActiveCustomersSection.module.scss";
import { getMostActiveCustomersQuery } from "./queries";
import { type MostActiveCustomerResponse } from "../../DataService/types";
import { Tooltip } from "components/common/tooltip";
import { TextTruncate } from "components";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { bindAction } from "interfaces";
import { connect, type ConnectedProps } from "react-redux";
import { type AppDispatch } from "features/Application/globaltypes/redux";
import { useEffect } from "react";

type Props = {
  templateId: number;
  filter: PerformanceWithAccountFilter;
} & PropsFromRedux;

const columns: ColumnOption<MostActiveCustomerResponse>[] = [
  {
    name: "Company Name",
    width: 5,
    isSortable: false,
    render: (item) => (
      <Tooltip
        target={
          <TextTruncate>
            <MoboLink className={styles.linkButton} target="_self" to={`/accounts/linked/${item.AccountId}`}>
              {item.AccountName}
            </MoboLink>
          </TextTruncate>
        }
        content={item.AccountName}
      />
    ),
  },
  {
    name: "Enabled Flows",
    width: 4,
    isSortable: false,
    render: (item) => item.FlowEnabled.toLocaleString(),
  },
  {
    name: "Prioritized Flows",
    width: 4,
    isSortable: false,
    render: (item) => item.FlowPriority.toLocaleString(),
  },
  {
    name: "Flows Starts",
    width: 4,
    isSortable: false,
    render: (item) => item.FlowStarted.toLocaleString(),
  },
  {
    name: "Flows Completions",
    width: 4,
    isSortable: false,
    render: (item) => item.FlowCompleted.toLocaleString(),
  },
  {
    name: "Flows Not Started",
    width: 4,
    isSortable: false,
    render: (item) => item.FlowNotStarted.toLocaleString(),
  },
  {
    name: "Users",
    width: 3,
    isSortable: false,
    render: (item) => item.UserCount.toLocaleString(),
  },
  {
    name: "% Users W/ Interactions",
    width: 5,
    isSortable: false,
    render: (item) => `${Math.round(item.PercentageWithInteraction * 100)}%`,
  },
  {
    name: "Avg Interactions/User",
    width: 5,
    isSortable: false,
    render: (item) => roundToTwoDigits(item.AverageInteractions),
  },
];

export const MostActiveCustomersSection: React.FC<Props> = ({ templateId, filter, setHasExportData }) => {
  const mostActiveCustomersQuery = useQuery({
    queryKey: ["templateMostActiveCustomers", templateId, filter],
    queryFn: getMostActiveCustomersQuery,
    enabled: filter.type !== undefined,
  });

  useEffect(() => {
    setHasExportData({
      hasDataToExport: mostActiveCustomersQuery.isSuccess && mostActiveCustomersQuery.data.length !== 0,
      tooltipContent: "No data available for export. This template contains no user activity data.",
    });
  }, [setHasExportData, mostActiveCustomersQuery.isSuccess, mostActiveCustomersQuery.data]);

  return (
    <div className={styles.mostActiveCustomersSection}>
      <h5>Most Active Customers</h5>
      <div className={styles.tableWrapper}>
        <RequestStatusRenderer state={mostActiveCustomersQuery.status}>
          <GenericPerformanceList
            rows={mostActiveCustomersQuery.data!}
            columns={columns}
            filter={filter}
            key="templateMostActiveCustomers"
          />
        </RequestStatusRenderer>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
});

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
const ConnectedComponent = connector(MostActiveCustomersSection);

export default ConnectedComponent;
